import React, { useState, useEffect, useMemo } from "react";
import { Box, Text, VStack, HStack, Link } from "@chakra-ui/react";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { RfxDashboardItem } from "./components/RfxDashboardItem";
import { DashboardRfxItemData } from "./types/DashboardRfxItemData";
import { DashboardItemProjectData } from "./types/DashboardItemProjectData";
import { useSelector } from "react-redux";
import { ApiResponse } from "types/common.types";
import { apiV1 } from "api-v2";
import { hasSubmissionDeadlinePassed } from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import moment from "moment";
import { Icon } from "assets/Icon";
import Tooltip from "components/Tooltip";
import { Switch } from "components/Switch";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { getCurrentCompanyId, getProjectUrl } from "helpers/linkHelper";

dayjs.extend(relativeTime);

const ProcurerDashboard: React.FC = () => {
	const [data, setData] = useState<DashboardRfxItemData[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [isViewByProject, setViewByProject] = useState(false);
	// @ts-ignore TODO: FIX up state casting
	const user = useSelector((state) => state.auth.user);
	const companyId = getCurrentCompanyId();

	useEffect(() => {
		if (!loading) {
			return;
		}

		apiV1
			.get<ApiResponse<DashboardRfxItemData[]>>(
				`/dashboard/company/${companyId}/user/${user.id}`
			)
			.then((response) => {
				const data = response.data.data?.map((rfxItem) => ({
					...rfxItem,
					submissionDeadline: new Date(rfxItem.submissionDeadline),
					hasReachedDeadline: hasSubmissionDeadlinePassed(
						moment(rfxItem.submissionDeadline),
						rfxItem.deadlineTimeZone
					),
				}));
				setData(data);
			})
			.catch((error) => {
				setData(null);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [companyId, loading, user.id]);

	const handleRefresh = () => {
		setLoading(true);
	};

	const handleViewChange = () => {
		const newValue = !isViewByProject;
		setViewByProject(newValue);
	};

	const sortByDeadline = (a: DashboardRfxItemData, b: DashboardRfxItemData) => {
		return a.submissionDeadline.getTime() - b.submissionDeadline.getTime();
	};

	const projectData = useMemo(() => {
		const getProjectData = (
			items: DashboardRfxItemData[]
		): DashboardItemProjectData[] => {
			const projectMap = new Map<string, DashboardItemProjectData>();

			const sortedItems = items.sort(sortByDeadline);

			for (const item of sortedItems) {
				const existing = projectMap.get(item.projectId.toString());
				if (existing) {
					existing.rfxItems.push(item);
				} else {
					projectMap.set(item.projectId.toString(), {
						projectId: item.projectId,
						projectName: item.projectName,
						rfxItems: [item],
					});
				}
			}

			return Array.from(projectMap.values());
		};

		return isViewByProject && data ? getProjectData(data) : undefined;
	}, [isViewByProject, data]);

	const sortedData = useMemo(() => {
		return !isViewByProject && data
			? [...data].sort(sortByDeadline)
			: undefined;
	}, [isViewByProject, data]);

	const header = (
		<HStack className="mb-6 text-2xl font-bold text-gray-800">
			<Text>My Dashboard</Text>
			{!loading && (
				<Tooltip
					label="Refresh dashboard Data"
					shouldWrapChildren
				>
					<button onClick={handleRefresh}>
						<Icon
							icon="refresh"
							className="cursor-pointer text-gray-500"
						></Icon>
					</button>
				</Tooltip>
			)}
		</HStack>
	);

	if (loading) {
		return (
			<Box className="mx-auto max-w-7xl p-6">
				{header}
				<Text>Loading dashboard...</Text>
				<LoadingSpinner />
			</Box>
		);
	}

	if (!data) {
		return (
			<Box className="mx-auto max-w-7xl p-6">
				{header}
				<Text>No data available</Text>
			</Box>
		);
	}

	return (
		<Box className="mx-auto max-w-7xl p-6">
			{header}

			<Box>
				<Switch
					id="viewType"
					label="View dashboard by Project?"
					isChecked={isViewByProject}
					onChange={handleViewChange}
					size="md"
					className="pb-5"
				/>
			</Box>

			{!isViewByProject &&
				sortedData?.map((item, index) => (
					<Box className="mb-2 rounded-lg border border-gray-200 bg-white p-4">
						<RfxDashboardItem
							key={item.projectId}
							item={item}
							companyId={companyId}
							showSupplierGuests={data.length === 1}
							viewByProject={isViewByProject}
						/>
					</Box>
				))}

			{isViewByProject &&
				projectData?.map((item, index) => (
					<Box
						key={item.projectId}
						className="mb-6"
						minW={333}
					>
						<Box className="rounded-t-lg bg-gray-100 p-4">
							<HStack justify="space-between">
								<VStack
									align="start"
									spacing={1}
								>
									<Text className="text-sm text-gray-600">Project Name:</Text>
									<Link
										href={getProjectUrl(
											companyId,
											item.projectId,
											item.projectName
										)}
										target="_blank"
									>
										<HStack>
											<Text className="font-bold text-gray-800">
												{item.projectName}
											</Text>
											<Icon
												icon={"open-in-new-window"}
												style={{ width: "25px", height: "25px" }}
											/>
										</HStack>
									</Link>
								</VStack>
							</HStack>
						</Box>

						{item.rfxItems.map((rfxItem, index) => (
							<Box className="rounded-b-lg border-x border-b border-gray-200 bg-white p-4">
								<RfxDashboardItem
									item={rfxItem}
									companyId={companyId}
									showSupplierGuests={data.length === 1}
									viewByProject={isViewByProject}
								/>
							</Box>
						))}
					</Box>
				))}
		</Box>
	);
};

export default ProcurerDashboard;
